import { sanitizeFileUrl } from "./filePreview.js";
import {
  buildPdfIframeSrc,
  fetchProtectedPreviewUrl,
  isProtectedPreviewUrl,
} from "./exportPdfPreview.js";

function isBlobUrl(url = "") {
  return String(url || "").startsWith("blob:");
}

/**
 * Öffnet eine Datei in einem neuen Tab.
 *
 * Wichtig: Geschützte /api- und /export-URLs direkt navigieren (Session-Cookie),
 * nicht als blob: öffnen. Blob-URLs haben keinen Dateinamen – Chrome lädt dann
 * z. B. Office-Dateien als UUID ohne Endung herunter.
 *
 * @returns {Promise<boolean>} ob ein Tab geöffnet wurde
 */
export async function openProtectedFileInNewTab(
  url = "",
  { existingBlobUrl = "" } = {},
) {
  if (typeof window === "undefined") return false;

  const safe = sanitizeFileUrl(url);

  // Echte Same-Origin-URL bevorzugen (Content-Disposition liefert den Dateinamen).
  if (safe && !isBlobUrl(safe)) {
    return Boolean(window.open(safe, "_blank"));
  }

  if (isBlobUrl(existingBlobUrl)) {
    return Boolean(window.open(existingBlobUrl, "_blank"));
  }

  if (isBlobUrl(safe)) {
    return Boolean(window.open(safe, "_blank"));
  }

  return false;
}

/**
 * Startet einen Download per Blob-URL (Session-Cookie für /api/…).
 */
export async function downloadProtectedFile(
  url = "",
  fileName = "datei",
  { existingBlobUrl = "", signal } = {},
) {
  if (typeof document === "undefined") return false;

  const safe = sanitizeFileUrl(url);
  let blobUrl = isBlobUrl(existingBlobUrl) ? existingBlobUrl : "";

  if (!blobUrl) {
    if (!safe) return false;
    if (isProtectedPreviewUrl(safe)) {
      blobUrl = await fetchProtectedPreviewUrl(safe, { signal });
    } else {
      blobUrl = safe;
    }
  }

  if (!blobUrl) return false;

  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = String(fileName || "datei").replace(/[\r\n"]/g, "_");
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}

async function resolveProtectedBlobUrl(
  url = "",
  { existingBlobUrl = "", signal } = {},
) {
  let blobUrl = isBlobUrl(existingBlobUrl) ? existingBlobUrl : "";

  if (!blobUrl) {
    const safe = sanitizeFileUrl(url);
    if (!safe) return "";
    if (isProtectedPreviewUrl(safe)) {
      blobUrl = (await fetchProtectedPreviewUrl(safe, { signal })) || "";
    } else {
      blobUrl = safe;
    }
  }

  return blobUrl || "";
}

/**
 * Öffnet den System-Druckdialog für eine geschützte PDF-Datei.
 * @returns {Promise<boolean>}
 */
export async function printProtectedFile(
  url = "",
  { existingBlobUrl = "", signal } = {},
) {
  if (typeof document === "undefined") return false;

  const blobUrl = await resolveProtectedBlobUrl(url, { existingBlobUrl, signal });
  if (!blobUrl) return false;

  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Druckvorschau");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none";
    iframe.src = buildPdfIframeSrc(blobUrl);

    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.setTimeout(() => iframe.remove(), 1500);
      resolve(ok);
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        finish(true);
      } catch {
        finish(false);
      }
    };
    iframe.onerror = () => finish(false);

    document.body.appendChild(iframe);
    window.setTimeout(() => finish(false), 30_000);
  });
}
